import { AxiosError } from 'axios';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { ErrorHttpResponse, SuccessHttpResponse } from '../../../models/https';
import { RiskSettings, RiskThresholds } from '../../../models/risk/risk.model';
import useHttps from '../../useHttps';

const errorMessage = (err: AxiosError<ErrorHttpResponse>) => {
  // Request-validation errors (422) skip the {status, message, data}
  // envelope: FastAPI returns {detail: [{msg, loc}, ...]} directly.
  const data = err?.response?.data as any;
  const detail = data?.detail ?? data?.message;
  if (Array.isArray(detail)) {
    return detail.map((d) => String(d?.msg ?? d).replace(/^Value error, /, '')).join('; ');
  }
  return typeof detail === 'string' ? detail : 'Could not save settings';
};

const useRiskSettings = () => {
  const request = useHttps();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<RiskSettings | null>(null);

  const fetchSettings = () => {
    request(
      { url: 'api/v1/admin/risk/settings', method: 'get' },
      ({ data }: SuccessHttpResponse<RiskSettings>) => setSettings(data),
      setLoading,
      (err: AxiosError<ErrorHttpResponse>) => console.error(errorMessage(err))
    );
  };

  const saveSettings = (
    thresholds: RiskThresholds,
    disabledRules: string[],
    onDone?: () => void
  ) => {
    request(
      {
        url: 'api/v1/admin/risk/settings',
        method: 'put',
        data: { thresholds, disabled_rules: disabledRules },
      },
      ({ data }: SuccessHttpResponse<RiskSettings>) => {
        setSettings(data);
        toast.success('Rule settings saved');
        onDone?.();
      },
      setSaving,
      (err: AxiosError<ErrorHttpResponse>) => toast.error(errorMessage(err))
    );
  };

  return { settings, loading, saving, fetchSettings, saveSettings };
};

export default useRiskSettings;
